export { slugify, getProductSlug, getProductPath } from './productSlug';

export const DEFAULT_PRODUCT_IMAGE =
  'https://st2.depositphotos.com/4060975/8113/v/450/depositphotos_81132582-stock-illustration-vinyl-colored-vector-icon.jpg';

const CLOUDINARY_UPLOAD_SEGMENT = '/upload/';

const IMAGE_TRANSFORMS = {
  card: 'f_auto,q_auto,c_fill,w_420,h_420,dpr_auto',
  detail: 'f_auto,q_auto,c_limit,w_900,h_900,dpr_auto',
  thumb: 'f_auto,q_auto,c_fill,w_120,h_120,dpr_auto',
};

const IMAGE_SRC_SET_TRANSFORMS = {
  card: [
    { width: 180, transform: 'f_auto,q_auto,c_fill,w_180,h_180' },
    { width: 240, transform: 'f_auto,q_auto,c_fill,w_240,h_240' },
    { width: 360, transform: 'f_auto,q_auto,c_fill,w_360,h_360' },
    { width: 420, transform: 'f_auto,q_auto,c_fill,w_420,h_420' },
    { width: 540, transform: 'f_auto,q_auto,c_fill,w_540,h_540' },
    { width: 640, transform: 'f_auto,q_auto,c_fill,w_640,h_640' },
    { width: 840, transform: 'f_auto,q_auto,c_fill,w_840,h_840' },
  ],
  detail: [
    { width: 480, transform: 'f_auto,q_auto,c_limit,w_480,h_480' },
    { width: 900, transform: 'f_auto,q_auto,c_limit,w_900,h_900' },
    { width: 1200, transform: 'f_auto,q_auto,c_limit,w_1200,h_1200' },
  ],
  thumb: [
    { width: 80, transform: 'f_auto,q_auto,c_fill,w_80,h_80' },
    { width: 120, transform: 'f_auto,q_auto,c_fill,w_120,h_120' },
    { width: 180, transform: 'f_auto,q_auto,c_fill,w_180,h_180' },
  ],
};

export const getCloudinaryImageUrl = (imageUrl, transform) => {
  if (!imageUrl || !transform) return imageUrl;

  const uploadIndex = imageUrl.indexOf(CLOUDINARY_UPLOAD_SEGMENT);

  if (uploadIndex === -1) return imageUrl;

  const insertAt = uploadIndex + CLOUDINARY_UPLOAD_SEGMENT.length;
  const afterUpload = imageUrl.slice(insertAt);

  if (afterUpload.startsWith(`${transform}/`)) return imageUrl;

  return `${imageUrl.slice(0, insertAt)}${transform}/${afterUpload}`;
};

export const getProductImageUrl = (imageUrl, variant = 'card') => {
  const source = imageUrl || DEFAULT_PRODUCT_IMAGE;
  const transform = IMAGE_TRANSFORMS[variant];

  return getCloudinaryImageUrl(source, transform);
};

export const getProductImageSrcSet = (imageUrl, variant = 'card') => {
  const source = imageUrl || DEFAULT_PRODUCT_IMAGE;
  const transforms = IMAGE_SRC_SET_TRANSFORMS[variant];

  if (!transforms || !source.includes(CLOUDINARY_UPLOAD_SEGMENT)) return undefined;

  return transforms
    .map(({ width, transform }) => `${getCloudinaryImageUrl(source, transform)} ${width}w`)
    .join(', ');
};

export const getProductImageSizes = (variant = 'card') => {
  if (variant === 'detail') {
    return '(max-width: 900px) 100vw, 50vw';
  }

  if (variant === 'thumb') {
    return '120px';
  }

  // Match the Container padding, card borders, grid gaps and desktop Top sidebar.
  if (variant === 'showcase') {
    return '(min-width: 1440px) 163px, (min-width: 1200px) 207px, (min-width: 900px) calc((100vw - 94.4px) / 4), (min-width: 600px) 218px, calc((100vw - 48px) / 2)';
  }

  return '(min-width: 1440px) 163px, (min-width: 1200px) 204px, (min-width: 900px) calc((100vw - 104px) / 4), (min-width: 760px) calc((100vw - 86px) / 3), (min-width: 600px) calc((100vw - 68px) / 2), calc((100vw - 45.6px) / 2)';
};

export const formatPrice = (value) =>
  new Intl.NumberFormat('es-AR').format(Number(value || 0));

export const getProductPriceValue = (product = {}) => {
  const price = Number(product?.price);

  return Number.isFinite(price) && price > 0 ? price : null;
};

export const getProductDisplayName = (product = {}) => {
  const artist = String(product.artist || '').trim();
  const title = String(product.title || product.name || '').trim();

  return [artist, title].filter(Boolean).join(' - ') || 'Disco sin nombre';
};

export const getProductStock = (product = {}) => {
  const parsedStock = Number(product?.stock ?? 0);
  return Number.isNaN(parsedStock) ? 0 : parsedStock;
};

export const isProductAvailable = (product = {}) => getProductStock(product) > 0;

export const getProductPriceLabel = (product = {}) => {
  if (!isProductAvailable(product)) return 'Sin stock';

  const price = getProductPriceValue(product);
  return price === null ? 'Consultar' : `$${formatPrice(price)}`;
};

export const compareProductsByPrice = (productA, productB, direction = 'asc') => {
  const priceA = isProductAvailable(productA)
    ? getProductPriceValue(productA)
    : null;
  const priceB = isProductAvailable(productB)
    ? getProductPriceValue(productB)
    : null;

  if (priceA === null && priceB === null) return 0;
  if (priceA === null) return 1;
  if (priceB === null) return -1;

  return direction === 'desc' ? priceB - priceA : priceA - priceB;
};

export const getTopSelectionRankById = (products = [], limit = 10) =>
  new Map(
    products
      .filter((product) => isProductAvailable(product))
      .slice(0, limit)
      .map((product, index) => [
        String(product._id || product.id),
        index + 1,
      ])
  );

export const getProductStatusLabel = (product = {}) =>
  isProductAvailable(product) ? 'Disponible' : 'Sin stock';

export const getProductConditionValue = (product = {}) =>
  product?.condition === 'new' ? 'new' : 'used';

export const getProductConditionLabel = (product = {}) =>
  getProductConditionValue(product) === 'new' ? 'Nuevo' : 'Usado';

export const getProductMediaType = (product = {}) =>
  product?.mediaType === 'cd' ? 'cd' : 'vinyl';

export const getMediaTypeLabel = (mediaType) =>
  mediaType === 'cd' ? 'CD' : 'Vinilo';

const getFeaturedOrderValue = (product = {}) => {
  if (
    product.featuredOrder === undefined ||
    product.featuredOrder === null ||
    product.featuredOrder === ''
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const parsedOrder = Number(product.featuredOrder);
  return Number.isFinite(parsedOrder)
    ? parsedOrder
    : Number.POSITIVE_INFINITY;
};

export const getTopSelectionProducts = (
  products = [],
  mediaType = 'vinyl',
  limit = 10
) => {
  const eligibleProducts = products
    .filter(
      (product) =>
        getProductMediaType(product) === mediaType &&
        isProductAvailable(product)
    )
    .sort((productA, productB) => {
      const dateA = productA.createdAt
        ? new Date(productA.createdAt).getTime()
        : 0;
      const dateB = productB.createdAt
        ? new Date(productB.createdAt).getTime()
        : 0;
      return dateB - dateA;
    });

  const featuredProducts = eligibleProducts
    .filter((product) => product.featured)
    .sort((productA, productB) => {
      const orderA = getFeaturedOrderValue(productA);
      const orderB = getFeaturedOrderValue(productB);

      if (orderA === orderB) return 0;
      return orderA - orderB;
    });
  const featuredIds = new Set(
    featuredProducts.map((product) => String(product._id || product.id))
  );
  const recentFallback = eligibleProducts.filter(
    (product) => !featuredIds.has(String(product._id || product.id))
  );

  return [...featuredProducts, ...recentFallback].slice(0, limit);
};

export const getProductPublicationStatusValue = (product = {}) =>
  product?.publicationStatus === 'draft' ? 'draft' : 'published';

export const getProductPublicationStatusLabel = (product = {}) =>
  getProductPublicationStatusValue(product) === 'draft' ? 'Borrador' : 'Publicado';

export const getProductVideos = (product = {}) => {
  const normalizedVideos = Array.isArray(product?.videos)
    ? product.videos
        .map((video, index) => {
          const title = String(video?.title || '').trim();
          const url = String(video?.url || '').trim();

          if (!url) return null;

          return {
            title: title || `Preview ${index + 1}`,
            url,
          };
        })
        .filter(Boolean)
    : [];

  if (normalizedVideos.length) {
    return normalizedVideos;
  }

  const fallbackUrl = String(product?.youtubeUrl || '').trim();

  if (!fallbackUrl) {
    return [];
  }

  return [
    {
      title: 'Preview 1',
      url: fallbackUrl,
    },
  ];
};

export const getYoutubeEmbedUrl = (url) => {
  if (!url) return '';

  try {
    const parsed = new URL(url);
    const embedParams = 'autoplay=1&playsinline=1&rel=0';

    if (parsed.hostname.includes('youtu.be')) {
      const videoId = parsed.pathname.replace('/', '');
      return `https://www.youtube.com/embed/${videoId}?${embedParams}`;
    }

    if (parsed.hostname.includes('youtube.com')) {
      const videoId = parsed.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?${embedParams}`;
      }

      const pathMatch = parsed.pathname.match(/\/(?:shorts|embed)\/([^/?]+)/);
      if (pathMatch?.[1]) {
        return `https://www.youtube.com/embed/${pathMatch[1]}?${embedParams}`;
      }
    }

    return '';
  } catch (error) {
    return '';
  }
};
